import React, { useState, useRef } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog'
import { Microphone, Stop, Sparkle, X, Plus } from '@phosphor-icons/react'
import api from '../services/api'
import { toast } from 'sonner'

const PLATFORMS = ['YouTube', 'Instagram', 'TikTok', 'LinkedIn']
const STATUSES = [
  { value: 'idea', label: 'Idea', color: '#FFD60A' },
  { value: 'plan', label: 'Plan', color: '#5E5CE6' },
  { value: 'record', label: 'Grabar', color: '#D42B1E' },
  { value: 'edit', label: 'Editar', color: '#FF9F0A' },
  { value: 'analyze', label: 'Analizar', color: '#007AFF' },
  { value: 'publish', label: 'Publicar', color: '#32ADE6' },
]

export default function AddProjectModal({ open, onClose, onCreated, editProject }) {
  const [form, setForm] = useState(
    editProject || {
      title: '',
      description: '',
      tags: [],
      platform: 'YouTube',
      duration_min: 5,
      status: 'idea',
      estimated_views: 0,
      engagement_score: 0,
      notes: '',
    }
  )
  const [tagInput, setTagInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [recording, setRecording] = useState(false)
  const [loadingHook, setLoadingHook] = useState(false)
  const [hooks, setHooks] = useState([])
  const [createAndWrite, setCreateAndWrite] = useState(true)
  const mediaRecorderRef = useRef(null)
  const chunksRef = useRef([])

  React.useEffect(() => {
    if (editProject) {
      setForm(editProject)
      setCreateAndWrite(false)
    } else {
      setForm({
        title: '',
        description: '',
        tags: [],
        platform: 'YouTube',
        duration_min: 5,
        status: 'idea',
        estimated_views: 0,
        engagement_score: 0,
        notes: '',
      })
      setCreateAndWrite(true)
    }
    setHooks([])
    setTagInput('')
  }, [editProject, open])

  const addTag = () => {
    const t = tagInput.trim().replace(/^#/, '')
    if (t && !form.tags.includes(t)) {
      setForm(f => ({ ...f, tags: [...f.tags, t] }))
    }
    setTagInput('')
  }

  const removeTag = tag => setForm(f => ({ ...f, tags: f.tags.filter(t => t !== tag) }))

  const handleTagKeyDown = e => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addTag()
    }
  }

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mr = new MediaRecorder(stream)
      chunksRef.current = []
      mr.ondataavailable = e => chunksRef.current.push(e.data)
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' })
        const formData = new FormData()
        formData.append('file', blob, 'note.webm')
        try {
          const res = await api.post('/voice/transcribe', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
          if (res.data.text) {
            setForm(f => ({
              ...f,
              notes: f.notes ? f.notes + ' ' + res.data.text : res.data.text,
            }))
            toast.success('Nota de voz transcrita')
          } else {
            toast.error('No se pudo transcribir el audio')
          }
        } catch (err) {
          toast.error('Error al transcribir')
        }
        stream.getTracks().forEach(t => t.stop())
      }
      mr.start()
      mediaRecorderRef.current = mr
      setRecording(true)
    } catch (err) {
      toast.error('No se pudo acceder al micrófono')
    }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop()
      setRecording(false)
    }
  }

  const generateHooks = async () => {
    if (!form.title.trim()) {
      toast.error('Añade un título primero')
      return
    }
    setLoadingHook(true)
    try {
      const res = await api.post('/ai/hook', {
        title: form.title,
        description: form.description,
        platform: form.platform,
      })
      setHooks(res.data.hooks || [])
    } catch (err) {
      toast.error('Error al generar hooks')
    } finally {
      setLoadingHook(false)
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()
    if (!form.title.trim()) {
      toast.error('El título es requerido')
      return
    }
    setLoading(true)
    try {
      let result
      if (editProject) {
        result = await api.put(`/projects/${editProject.id}`, form)
        toast.success('Proyecto actualizado')
      } else {
        result = await api.post('/projects', form)
        toast.success('¡Vídeo creado!')
      }
      onCreated?.(result.data, !editProject && createAndWrite)
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="bg-ds-surface border border-ds-border text-ds-text max-w-lg max-h-[90vh] overflow-y-auto"
        style={{ borderRadius: 2 }}
        data-testid="add-project-modal"
      >
        <DialogHeader>
          <DialogTitle className="type-h3 text-ds-text text-base">
            {editProject ? 'Editar Proyecto' : 'Nueva Idea de Video'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          {/* Title */}
          <div>
            <label className="ds-label">Título *</label>
            <input
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="Ej: Tutorial de edición en DaVinci"
              className="ds-input"
              required
              data-testid="project-title-input"
            />
          </div>

          {/* Description */}
          <div>
            <label className="ds-label">Descripción</label>
            <textarea
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="¿De qué trata este video?"
              rows={3}
              className="ds-input resize-none"
              data-testid="project-description-input"
            />
          </div>

          {/* Platform + Status */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="ds-label">Plataforma</label>
              <select
                value={form.platform}
                onChange={e => setForm(f => ({ ...f, platform: e.target.value }))}
                className="ds-select"
                data-testid="project-platform-select"
              >
                {PLATFORMS.map(p => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="ds-label">Estado</label>
              <select
                value={form.status}
                onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                className="ds-select"
                data-testid="project-status-select"
              >
                {STATUSES.map(s => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Duration + Views */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="ds-label">Duración (min)</label>
              <input
                type="number"
                value={form.duration_min}
                onChange={e =>
                  setForm(f => ({ ...f, duration_min: parseInt(e.target.value) || 0 }))
                }
                min={1}
                max={120}
                className="ds-input"
                data-testid="project-duration-input"
              />
            </div>
            <div>
              <label className="ds-label">Views Estimadas</label>
              <input
                type="number"
                value={form.estimated_views}
                onChange={e =>
                  setForm(f => ({ ...f, estimated_views: parseInt(e.target.value) || 0 }))
                }
                min={0}
                className="ds-input"
                data-testid="project-views-input"
              />
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="ds-label">Tags</label>
            <div className="flex gap-2 mb-2">
              <input
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                placeholder="#filmmaker #tutorial"
                className="ds-input flex-1"
                data-testid="project-tags-input"
              />
              <button
                type="button"
                onClick={addTag}
                className="btn-ghost-sm px-3"
                data-testid="add-tag-btn"
              >
                <Plus size={14} />
              </button>
            </div>
            {form.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {form.tags.map(tag => (
                  <span key={tag} className="inline-flex items-center gap-1 badge-muted">
                    #{tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="text-ds-muted hover:text-ds-red transition-colors"
                      data-testid={`remove-tag-${tag}`}
                    >
                      <X size={10} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Notes + Voice */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="ds-label mb-0">Notas</label>
              <button
                type="button"
                onClick={recording ? stopRecording : startRecording}
                className={`relative flex items-center gap-1.5 px-2.5 py-1 type-btn text-[11px] transition-all ${
                  recording
                    ? 'bg-[rgba(212,43,30,0.12)] text-ds-red border border-[rgba(212,43,30,0.3)]'
                    : 'bg-ds-elevated text-ds-muted border border-ds-border hover:text-ds-text'
                }`}
                style={{ borderRadius: 2 }}
                data-testid="voice-note-btn"
              >
                {recording ? (
                  <Stop size={12} className="animate-pulse" />
                ) : (
                  <Microphone size={12} />
                )}
                {recording ? 'Detener' : 'Nota voz'}
              </button>
            </div>
            <textarea
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Ideas, referencias, notas adicionales..."
              rows={2}
              className="ds-input resize-none"
              data-testid="project-notes-textarea"
            />
          </div>

          {/* AI Hook Generator */}
          <div
            className="border border-[rgba(0,122,255,0.2)] p-3 bg-[rgba(0,122,255,0.04)]"
            style={{ borderRadius: 2 }}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="type-caption text-[#007AFF] flex items-center gap-1.5">
                <Sparkle size={11} weight="fill" /> Generar Hooks IA
              </span>
              <button
                type="button"
                onClick={generateHooks}
                disabled={loadingHook}
                className="type-btn text-[11px] bg-[#007AFF] text-white px-3 py-1 hover:bg-[#0066DD] disabled:opacity-50 transition-colors"
                style={{ borderRadius: 2 }}
                data-testid="generate-hooks-btn"
              >
                {loadingHook ? 'Generando...' : 'Generar'}
              </button>
            </div>
            {hooks.length > 0 && (
              <div className="space-y-1.5">
                {hooks.map((hook, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, notes: hook }))}
                    className="w-full text-left type-caption text-ds-muted hover:text-ds-text bg-ds-elevated px-3 py-2 transition-colors border border-ds-border hover:border-ds-border-mid"
                    style={{ borderRadius: 2 }}
                    data-testid={`hook-option-${i}`}
                  >
                    "{hook}"
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Create & write toggle — only for new projects */}
          {!editProject && (
            <button
              type="button"
              onClick={() => setCreateAndWrite(v => !v)}
              className={`w-full flex items-center gap-3 px-4 py-3 border transition-all ${
                createAndWrite
                  ? 'border-[rgba(212,43,30,0.35)] bg-[rgba(212,43,30,0.06)]'
                  : 'border-ds-border bg-ds-elevated hover:border-ds-border-mid'
              }`}
              style={{ borderRadius: 2 }}
              data-testid="create-and-write-toggle"
            >
              <div
                className={`w-4 h-4 border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                  createAndWrite ? 'bg-ds-red border-ds-red' : 'border-ds-muted'
                }`}
                style={{ borderRadius: 2 }}
              >
                {createAndWrite && (
                  <svg width="8" height="6" viewBox="0 0 10 8" fill="none">
                    <path
                      d="M1 4L3.5 6.5L9 1"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </div>
              <div className="text-left">
                <p
                  className={`text-sm font-medium ${createAndWrite ? 'text-ds-text' : 'text-ds-muted'}`}
                  style={{ fontFamily: 'Barlow, sans-serif' }}
                >
                  Ir al guión ahora
                </p>
                <p
                  className="text-ds-muted text-[11px]"
                  style={{ fontFamily: 'Barlow, sans-serif' }}
                >
                  Abre el editor de guión después de crear
                </p>
              </div>
            </button>
          )}

          {/* Submit */}
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="btn-ghost flex-1 py-2.5"
              data-testid="cancel-project-btn"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-accent flex-1 py-2.5 disabled:opacity-50"
              data-testid="save-project-btn"
            >
              {loading
                ? 'Guardando...'
                : editProject
                  ? 'Actualizar'
                  : createAndWrite
                    ? 'Crear y escribir →'
                    : 'Crear vídeo'}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
